import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiChevronLeft, FiMoreHorizontal, FiPlus } from 'react-icons/fi';
import { DashboardSidebar } from '@/layouts/dashboard';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import Swal from 'sweetalert2';
import { useEffect } from 'react';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { t } from 'i18next';
import IconVerified from '@/app/components/Icons/IconVerified';
import { useSession } from '@/hooks/useSession';
import usePermission from '@/hooks/usePermission';
import { ModuleList, ModuleListItem, ModuleListTitle } from '@/components/custom/module';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';

export default function Sidebar(props) {
  const navigate = useNavigate();
  const { course, currentModuleId, setCurrentModuleId, loading, setCount, selectedTab } = props;
  const client = useClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredModules, setFilteredModules] = useState([]);
  const [click, setClick] = useState(0);
  const { moduleId } = useParams();

  useEffect(() => {
    setCurrentModuleId(window?.history?.state?.moduleId ?? moduleId);
  }, [click]);

  useEffect(() => {
    if (course && course['learning-systems']) {
      const filtered = course['learning-systems'].filter(item => {
        if (searchTerm === '') {
          return true;
        } else if (item.title.toLowerCase().includes(searchTerm.toLowerCase())) {
          return true;
        }
        return false;
      });
      setFilteredModules(filtered);
    }
  }, [course, searchTerm]);

  const deleteModule = async item => {
    const { error } = await client.delete(`learning_systems/${item.id}`);
    if (error) {
      toast.error('Erro ao deletar módulo');
    } else {
      toast.success('Módulo excluido com sucesso!');
      const updatedModulesList = filteredModules.filter(module => module.id !== item.id);
      setFilteredModules(updatedModulesList);
      setCount(prevState => prevState + 1);
      // window.history.pushState({ moduleId: course?.['learning-systems']?.[0]?.id }, '', `/curso/admin/gerenciar/${course?.id}/${course?.['learning-systems']?.[0]?.id}`);
      // setClick(prevState => prevState + 1);
    }
  };

  const shortByCreation = (a, b) => {
    const dateA = new Date(a?.['updated_at']);
    const dateB = new Date(b?.['updated_at']);
    return dateB - dateA;
  };

  filteredModules?.sort(shortByCreation);

  const handleDelete = item => {
    Swal.fire({
      title: t('projects.warningDeleteModule'),
      text: t('projects.warningDeleteCannotBeUndoneText'),
      confirmButtonText: t('button.yes'),
      showDenyButton: true,
      denyButtonText: t('button.no'),
      showCloseButton: true
    }).then(res => {
      if (res.isConfirmed) {
        deleteModule(item);
      }
    });
  };

  const currentUser = useSession()?.session?.user;
  const hasManagePermission = usePermission('Gerenciar conteúdo', 'Cursos');

  const hasModulePermission = module => {
    if (hasManagePermission && (!module?.['authored_by_user'] || (module?.['authored_by_user'] && currentUser.id === module?.['creator_id']) || currentUser?.profileNames.includes('Administrador'))) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <DashboardSidebar
      hasBrand={false}
      viewMode="create"
    >
      <nav className="notebook-sidebar__nav">
        <div className="filter-bar">
          <button
            className="filter-bar__back"
            onClick={() => navigate('/curso/admin')}
          >
            <div className="card__icon-wrapper">
              <FiChevronLeft />
            </div>
            <span
              className="filter-bar__back-container"
              title={course?.title}
            >
              <span className="filter-bar__title">{course?.title}</span>
            </span>
          </button>
        </div>

        <div className="form__row">
          <label
            htmlFor="moduleSearch"
            className="form__label"
          >
            {t('textsCommon.modules')}
          </label>
          <input
            aria-label="Pesquisar módulos"
            className="form__control form__control--search-with-icon"
            placeholder={t('textsCommon.research')}
            type="search"
            name="moduleSearch"
            id="moduleSearch"
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {course?.id ? (
          <Link to={`/lms/criar/modulo/nome/${course?.id}`}>
            <button className="btn btn--outline-dashed btn--small btn--full">
              <FiPlus className="btn__icon" /> {t('button.newModule')}
            </button>
          </Link>
        ) : (
          <Loader />
        )}

        <hr className="u-hr" />

        <ModuleList>
          {loading && <Loader />}

          {!loading && filteredModules.length === 0 && <EmptyState type="data" />}

          {!loading &&
            filteredModules?.map(item => (
              <ModuleListItem
                key={item.id}
                onClick={() => {
                  if (hasModulePermission(item)) {
                    setClick(prevState => prevState + 1);
                    navigate(`/curso/admin/gerenciar/${course?.id}/${item.id}/${selectedTab}/d`);
                  }
                }}
                active={currentModuleId == item.id}
                style={!hasModulePermission(item) ? { cursor: 'default' } : {}}
              >
                <ModuleListTitle>
                  {item?.official_content && <IconVerified />} {item.title}
                </ModuleListTitle>

                <div onClick={e => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button">
                        <FiMoreHorizontal size={16} />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      align="end"
                      style={{ zIndex: 9999 }}
                    >
                      <DropdownMenuItem onSelect={() => navigate(`/show-course/${item.id}/classroom`)}>{t('button.view')}</DropdownMenuItem>

                      {hasModulePermission(item) && (
                        <>
                          <DropdownMenuItem onSelect={() => navigate(`/lms/criar/modulo/nome/${item.id}/${course?.id}`)}>{t('button.edit')}</DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-danger"
                            onSelect={() => handleDelete(item)}
                          >
                            {t('button.delete')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </ModuleListItem>
            ))}
        </ModuleList>
      </nav>
    </DashboardSidebar>
  );
}
